import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { motion } from 'framer-motion';
import { X, Volume2, VolumeX } from 'lucide-react';
import { BADGE_CATALOG } from '../utils/badges';

/**
 * Full-screen celebration when a user unlocks a milestone badge that has a
 * `celebration_video` in the catalog. The video auto-plays WITH SOUND; if
 * the browser blocks autoplay-with-audio (rare since the modal opens after
 * a user action like completing a workout, but Safari can be strict), we
 * fall back to muted playback and surface a "tap to unmute" button so the
 * user can opt-in with a single tap.
 */
export const BadgeCelebrationModal = ({ isOpen, badgeCode, onClose }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const meta = badgeCode ? BADGE_CATALOG[badgeCode] : null;

  useEffect(() => {
    if (!isOpen || !videoRef.current) return;
    const v = videoRef.current;
    v.currentTime = 0;
    v.muted = false;
    setMuted(false);
    // Try unmuted autoplay first. If the browser refuses (NotAllowedError),
    // fall back to muted autoplay so the visuals still play, and flip state
    // so the user can manually unmute via the speaker button.
    v.play().catch(() => {
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
  }, [isOpen]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) v.play().catch(() => {});
  };

  if (!meta) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-black border-primary overflow-hidden p-0"
        data-testid="badge-celebration-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
          data-testid="badge-celebration-close-btn"
          aria-label="close"
        >
          <X className="h-5 w-5 text-white" />
        </button>

        {meta.celebration_video && (
          <button
            onClick={toggleMute}
            className="absolute top-4 left-4 z-20 h-10 px-3 rounded-full bg-black/60 hover:bg-black/80 flex items-center gap-2 text-white text-xs uppercase tracking-wider"
            data-testid="badge-celebration-mute-btn"
            aria-label={muted ? 'unmute' : 'mute'}
          >
            {muted ? (
              <>
                <VolumeX className="h-4 w-4 text-yellow-400" /> Tap to unmute
              </>
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </button>
        )}

        <div className="relative">
          {meta.celebration_video ? (
            <video
              ref={videoRef}
              src={meta.celebration_video}
              autoPlay
              loop
              playsInline
              preload="auto"
              className="w-full h-[420px] object-cover"
            />
          ) : (
            <div className="w-full h-[420px] bg-gradient-to-br from-yellow-600/30 via-primary/10 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/95 pointer-events-none" />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="absolute bottom-0 left-0 right-0 p-8 text-center z-10"
          >
            <div className="font-heading text-[11px] uppercase tracking-[0.3em] text-primary mb-2">
              Badge Unlocked
            </div>
            <h2 className="font-heading text-4xl text-white uppercase tracking-wider mb-1">
              {meta.name}
            </h2>
            <p className="text-muted-foreground mb-5">{meta.description}</p>
            <Button
              onClick={onClose}
              className="bg-primary text-black hover:bg-primary/90 font-heading tracking-wider uppercase px-8"
              data-testid="badge-celebration-dismiss-btn"
            >
              Keep Going
            </Button>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
